import type {
  AgentConnection,
  AgentConnectionInput,
  AgentDto,
  PatchAgentBody,
  TriggerDto,
  TriggerInput,
} from '@/types/gtm-agents';
import { abilityOn, isReadTool, PAIRED_SETTING } from './agent-switches.util';

/**
 * What an edit on the agent panel writes, and the write that takes it back.
 * Pure, so the undo rules are tested here.
 */

/**
 * A write: the fields to send, or how to build them from the agent as it
 * stands at that moment, which a write to a whole field (limits, delivery,
 * tools) must use so it keeps the write made just before it.
 */
export type Change = PatchAgentBody | ((current: AgentDto) => PatchAgentBody);

/**
 * A connection as the PATCH writes it: what the agent stores. The API works
 * out `actsAs`, `actsAsOwner`, `connectedBy` and `fromPlay` itself and ignores
 * them in a write, so none of them is sent back. A read stored as Ask each
 * time goes back as Never, which is how it reads.
 */
export function connectionInput(
  connection: AgentConnectionInput
): AgentConnectionInput {
  return {
    type: connection.type,
    enabled: connection.enabled,
    actingUserId: connection.actingUserId,
    policy: Object.fromEntries(
      Object.entries(connection.policy).map(([tool, rule]) => [
        tool,
        isReadTool(tool) && rule === 'ask' ? 'never' : rule,
      ])
    ),
  };
}

/**
 * The connections the agent stores, as a PATCH writes them back. An app only
 * its plays use (`fromPlay`) isn't stored, so it isn't written until a change
 * stores it.
 */
export function storedConnectionInputs(
  connections: readonly AgentConnection[]
): AgentConnectionInput[] {
  return connections
    .filter((connection) => !connection.fromPlay)
    .map(connectionInput);
}

/** A trigger as the PATCH writes it back: the fire times are the server's. */
export function triggerInput(trigger: TriggerDto): TriggerInput {
  return {
    id: trigger.id,
    kind: trigger.kind,
    config: trigger.config,
    schedule: trigger.schedule,
    note: trigger.note,
    enabled: trigger.enabled,
    source: trigger.source,
  };
}

/**
 * The PATCH that puts back every field `body` writes, as `before` had it.
 * Null when the edit cannot be taken back that way: finishing the setup, the
 * first trigger (the server always keeps exactly one), or an agent's own
 * trigger, which nothing runs and can't be set again (W6.2).
 */
export function undoBodyFor(
  body: PatchAgentBody,
  before: AgentDto
): PatchAgentBody | null {
  const out: PatchAgentBody = {};
  const keys = Object.keys(body) as (keyof PatchAgentBody)[];
  if (!keys.length) return null;
  for (const key of keys) {
    switch (key) {
      case 'name':
        out.name = before.name;
        break;
      case 'description':
        out.description = before.description ?? '';
        break;
      case 'goal':
        out.goal = before.goal ?? '';
        break;
      case 'instructions':
        out.instructions = before.instructions ?? '';
        break;
      case 'selfEditInstructions':
        out.selfEditInstructions = before.selfEditInstructions;
        break;
      case 'aiManagedTriggers':
        out.aiManagedTriggers = before.aiManagedTriggers;
        break;
      case 'isFavorite':
        out.isFavorite = before.isFavorite;
        break;
      case 'skillRefs':
        out.skillRefs = before.skillRefs.map(({ kind, id }) => ({ kind, id }));
        break;
      case 'knowledge':
        out.knowledge = { ...before.knowledge };
        break;
      case 'connections':
        out.connections = storedConnectionInputs(before.connections);
        break;
      case 'tools':
        out.tools = { ...before.tools };
        break;
      case 'triggers':
        if (!before.triggers.length) return null;
        if (before.triggers.some((trigger) => trigger.source === 'agent'))
          return null;
        if (body.playbookId) {
          const target = before.triggers.find(
            (trigger) =>
              trigger.id === body.playbookId && trigger.source === 'play'
          );
          if (!target || !before.playIds.includes(body.playbookId)) return null;
          out.triggers = [triggerInput(target)];
        } else out.triggers = before.triggers.map(triggerInput);
        break;
      case 'playbookId':
        if (!body.triggers || !before.playIds.includes(body.playbookId ?? ''))
          return null;
        out.playbookId = body.playbookId;
        break;
      case 'limits':
        out.limits = { ...before.limits };
        break;
      case 'delivery':
        out.delivery = {
          messages: {
            ...before.delivery.messages,
            email: [...before.delivery.messages.email],
          },
          data: { ...before.delivery.data },
        };
        break;
      default:
        // `setupCompleted` only ever finishes the setup.
        return null;
    }
  }
  // A pair is one setting, and the API refuses a write whose halves disagree
  // (it sends `tools` with the pair read as one, and the column as stored).
  // Whichever half goes back goes back as the ability reads: off while either
  // half is, and its switch beside it says the same.
  for (const key of Object.keys(
    PAIRED_SETTING
  ) as (keyof typeof PAIRED_SETTING)[]) {
    const column = PAIRED_SETTING[key];
    if (out[column] === undefined) continue;
    const on = abilityOn(before, key);
    out[column] = on;
    if (out.tools) out.tools = { ...out.tools, [key]: on };
  }
  return out;
}
